import '@fortawesome/fontawesome-free/css/all.min.css';
import { useContext, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import YouTube from 'react-youtube';
import { addToWishlist } from '../../services/userServices';
import { IMAGE_BASE_URL } from '../../utils/api-client';
import ModalSkeleton from '../Common/ModalSkeleton';
import CartContext from './../../contexts/CartContext';
import ProductVarientModal from './ProductVariantModal';
import './VideoProductCard.css';

const VideoProductCard = ({ product, youtubeVideoUrl }) => {
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const userId = localStorage.getItem('userID');
  const product_ids = product.product_id;

  const handleAddToWishlist = async () => {
    if (isWishlistLoading) return;
    try {
      setIsWishlistLoading(true);
      if (userId) {
        const response = await addToWishlist({
          user_id_fk: userId,
          product_ids,
        });
        if (response.data.status === true) {
          toast.success(response.data.message);
        } else {
          toast.error(response.data.message);
          if (response.data.data) {
            toast.error(response.data.data.join(', '));
          }
        }
      } else if (!userId) {
        toast.info('You need to login first!');
      }
    } catch (error) {
      toast.error('Error adding product to wishlist: ' + error.message);
    } finally {
      setIsWishlistLoading(false);
    }
  };

  const id = product.product_id;
  const slug = product.product_slug;
  const product_slug = product.product_slug;
  const title = product.product_name;
  const price =
    product.varients && product.varients.length > 0
      ? product.varients[0].product_price
      : 0;
  const image = product.product_image;
  const finalPrice =
    product.varients && product.varients.length > 0
      ? product.varients[0].final_price
      : 0;
  const stock =
    product.varients && product.varients.length > 0
      ? product.varients[0].in_stock
      : 0;
  const rating = product.avg_rating;
  const ratingCount = product.count_rating;
  const subcat_slug = product.subcategory && product.subcategory.s_cat_slug;
  const { addToCart } = useContext(CartContext);

  // Calculate discount percentage
  const discountPercentage =
    finalPrice < price ? Math.round(((price - finalPrice) / price) * 100) : 0;

  const handleAddToCart = () => {
    if (product.varients.length === 1) {
      addToCart(product, 1, product.varients[0]);
      toast.success('Product added to cart!');
    } else {
      setShowVariantModal(true);
    }
  };

  // Updated YouTube player options to fix postMessage errors
  const opts = {
    playerVars: {
      autoplay: 1,
      mute: 1,
      controls: 0,
      modestbranding: 1,
      rel: 0,
      // Add these parameters to reduce API calls and postMessage errors
      enablejsapi: 0, // Disable JavaScript API to reduce postMessage calls
      origin: window.location.origin, // Set origin to current domain
      widget_referrer: window.location.origin,
    },
  };

  // Add error handling for YouTube player
  const onError = (event) => {
    console.warn('YouTube player error:', event.data);
    // Optionally hide video on error
    setShowVideo(false);
  };

  const onReady = (event) => {
    // Player is ready, you can add additional logic here if needed
    console.log('YouTube player ready');
  };

  const touchStartHandler = () => {
    setShowVideo(true);
  };

  const touchEndHandler = () => {
    setShowVideo(false);
  };

  return (
    <section className="video_card_container">
      <ModalSkeleton width={300} height={100} />

      <div className="video_product_card">
        {/* Product Image/Video Container */}
        <div
          className="video_product_image"
          onMouseEnter={() => setShowVideo(true)}
          onMouseLeave={() => setShowVideo(false)}
          onTouchStart={touchStartHandler}
          onTouchEnd={touchEndHandler}
        >
          {/* Video/Image Content */}
          <div className="video_media_content">
            {showVideo && youtubeVideoUrl ? (
              <div className="video_container">
                <YouTube
                  videoId={youtubeVideoUrl}
                  opts={opts}
                  className="video_youtube_player"
                  onError={onError}
                  onReady={onReady}
                />
              </div>
            ) : (
              <NavLink to={`/shop/${subcat_slug}/${slug}/`}>
                {!imageLoaded && <div className="video_image_skeleton"></div>}
                <img
                  src={`${IMAGE_BASE_URL}/${image}`}
                  alt={product.alt_content}
                  className={`video_image_fluid ${
                    imageLoaded ? 'video_loaded' : ''
                  }`}
                  onLoad={() => setImageLoaded(true)}
                />
              </NavLink>
            )}
          </div>
        </div>

        {/* Product Details */}
        <div className="video_product_details">
          <h5 className="w-100 video_product_title">
            <NavLink
              title={title}
              to={`/shop/${subcat_slug}/${product_slug}/`}
              className="text-decoration-none text-black"
            >
              {title}
            </NavLink>
          </h5>

          {/* Rating */}
          {rating > 0 && (
            <div className="video_product_rating_container">
              <span className="video_product_rating">
                <i className="fa fa-solid fa-star"></i>
                <span className="video_rating_value">
                  {rating ? rating.toFixed(1) : 0}
                </span>
              </span>
              <span className="video_product_rating_count">
                ({ratingCount})
              </span>
            </div>
          )}

          {/* Price Container */}
          <p className="align_center justify-content-between video_product_price_container mt-2">
            <span className="fs-15 video_product_price d-block">
              <span className="video_currency">Rs.</span>
              <span className="video_price_value">
                {finalPrice.toLocaleString()}
              </span>
              {finalPrice < price && (
                <span className="ms-2 text-muted text-decoration-line-through video_original_price">
                  Rs. {price.toLocaleString()}
                </span>
              )}
            </span>
          </p>

          {/* Action Buttons */}
          <div className="row">
            <div className="col-12 align_center justify-content-between video_product_actions">
              {stock < 1 ? (
                <button
                  type="button"
                  className="btn video_product_card_btn video_out_of_stock"
                  disabled
                >
                  <i className="fas fa-times me-2"></i>
                  Out of Stock
                </button>
              ) : (
                <button
                  type="button"
                  className="btn video_product_card_btn"
                  onClick={handleAddToCart}
                >
                  <i className="fas fa-shopping-cart me-2"></i>
                  Add to cart
                </button>
              )}

              <button
                className={`video_card_wishlist ${
                  isWishlistLoading ? 'video_loading' : ''
                }`}
                onClick={handleAddToWishlist}
                disabled={isWishlistLoading}
              >
                <i
                  className={`fas fa-heart ${
                    isWishlistLoading ? 'fa-spinner fa-spin' : ''
                  }`}
                ></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showVariantModal && (
        <ProductVarientModal
          id={id}
          slug={slug}
          varients={product.varients}
          onClose={() => setShowVariantModal(false)}
          show={showVariantModal}
          subcat_slug={subcat_slug}
          product={product}
        />
      )}
    </section>
  );
};

export default VideoProductCard;
